/**
 * Player Panel
 * RHS panel of the 3-panel gameplay layout — the current best hand on each
 * axis (actual cards), each with its own axis's 8-line potential breakdown
 * nested inside it — not a separate shared box. Hole cards and the 3x3
 * matrix both live in the middle panel now, not in here.
 */

import React, { useMemo } from 'react';
import {
  isRealCard,
  lineOverview,
  lineTakenAway,
  nextCardAdvice,
  resolveForView,
  sameLine,
  type Card,
  type GridCard,
  type GridPosition,
} from '@pokercubed/shared';
import { useGameStore } from '../../store/gameStore';
import PlayingCard from './PlayingCard';
import BestHandDisplay, { type LineRow } from './BestHandDisplay';
import { useBestHandsSoFar, getLineNumber } from '../../hooks/useBestHandsSoFar';
import { useLinePotentials } from '../../hooks/useLinePotentials';
import { formatCardShort, formatJewelName } from '../../utils/format';

/** Drops the "Jewel"/"a Jewel" wording — the Best Color box's own heading already says which axis this is. */
const dropJewelWord = (label: string): string =>
  label
    .replace(/^No Jewel Match$/, 'No Match')
    .replace(/^No jewel match yet$/, 'No match yet')
    .replace(/^Four of a Jewel/, 'Four of a Kind')
    .replace(/^Five of a Jewel/, 'Five of a Kind')
    .replace(/^Jewel /, '');

/** "a 6♣", "an A♦", "an 8♦", "a Ruby", "an Emerald" — by how the card or jewel is said aloud. */
const withArticle = (thing: string) => `${/^(A|8|E|O|I|U)/i.test(thing) ? 'an' : 'a'} ${thing}`;

/** "a Pair", "a Full House" — but "Two Pair", "Three of a Kind" (a number already). */
const handWithArticle = (hand: string) => (/^(Two|Three|Four|Five)\b/.test(hand) ? hand : `a ${hand}`);

interface PlayerPanelProps {
  /** Total pot — split Hi/Lo, half to each axis (poker-app-spec.md Section 3). */
  pot: number;
  /** True once the hand has ended and the viewer won the rank-axis half of the pot. */
  wonRank?: boolean;
  /** True once the hand has ended and the viewer won the jewel-axis half of the pot. */
  wonJewel?: boolean;
  /** Phones: one box with both hands side by side (cards, and the card each needs) instead of the 8 lines. */
  compact?: boolean;
}

/** Text in the phone box: sized to each half's width, 9-15px. */
const TEXT = 'clamp(9px, 7cqw, 15px)';

const PlayerPanel: React.FC<PlayerPanelProps> = ({ pot, wonRank, wonJewel, compact = false }) => {
  const { rank, jewel } = useBestHandsSoFar();
  const linePotentials = useLinePotentials();

  // The single card that would most improve the hand on each axis (phones
  // show only this; big screens also list the 8 lines) — and those 8 lines
  // one by one. Both worked out with the real scoring (nextCardAdvice,
  // lineOverview): every hidden cell tried with every card still out,
  // specials and all (Anchor's shapes, Snare/Flawed/Fray, the Seer's known
  // centre, Impersonator/Limbo resolving), so the list and the hint agree.
  const communityCards = useGameStore((state) => state.communityCards);
  const players = useGameStore((state) => state.players);
  const yourSeat = useGameStore((state) => state.yourSeat);
  const specialEffects = useGameStore((state) => state.specialCardEffects);
  const advice = useMemo(() => {
    const you = players.find((p) => p.seatPosition === yourSeat);
    const hole = (you?.holeCards ?? []).filter(isRealCard);
    if (hole.length !== 2) return null;
    // Every cell you can see — revealed, or privately (Seer).
    const known: GridCard[] = communityCards
      .filter((c) => c.card)
      .map((c) => ({ position: c.position as GridPosition, card: c.card as Card, revealed: true }));
    const visible = [...hole, ...known.map((g) => g.card)];
    const resolved = resolveForView(hole, known, specialEffects);
    return {
      rank: nextCardAdvice('rank', hole, known, visible, specialEffects),
      jewel: nextCardAdvice('jewel', hole, known, visible, specialEffects),
      // The line Snare/Fray/Flawed takes away as things stand.
      taken: {
        rank: lineTakenAway('rank', resolved, known, specialEffects),
        jewel: lineTakenAway('jewel', resolved, known, specialEffects),
      },
      // The 8-line list is only on big screens.
      lines: compact
        ? null
        : {
            rank: lineOverview('rank', hole, known, visible, specialEffects),
            jewel: lineOverview('jewel', hole, known, visible, specialEffects),
          },
    };
  }, [communityCards, players, yourSeat, specialEffects, compact]);

  // Each line: its hand once complete; with one cell left, the card that
  // would make it strongest; earlier than that, what's showing so far. A
  // line a special takes out of play (Crossed's diagonals) or takes away
  // (Snare/Flawed/Fray) can't win, and says so. Listed by the best each line
  // could still become (each keeps its number): a made hand and a
  // one-card-away hand of the same rank sort together, the made one first;
  // lines too early to call come after, by what's showing; lines that can't
  // win last.
  const lineRows = (axis: 'rank' | 'jewel'): LineRow[] => {
    const rows = advice?.lines?.[axis] ?? [];
    const named = (name: string) => (axis === 'rank' ? name : dropJewelWord(name));
    return [...rows.map((row, i): LineRow => {
      const line = i + 1;
      const early = linePotentials[i]?.[axis];
      if (!row.inPlay) return { line, status: 'out', label: 'Not in play', strength: -2 };
      if (row.takenAway) {
        // The card the hint names may free it (the special takes another line instead).
        const hint = advice?.[axis];
        if (hint?.best && sameLine(hint.best.hand.winningLine, row.line)) {
          const card = axis === 'rank' ? formatCardShort(hint.best.card) : formatJewelName(hint.best.card.jewel);
          return { line, status: 'partial', label: `${card} frees it → ${named(hint.best.hand.name)}`, strength: 1000 + hint.best.hand.ranking };
        }
        const final = hint?.unknownCells === 0;
        return { line, status: 'out', label: `${named(row.hand?.name ?? '')} · taken${final ? ' away' : ' for now'}`, strength: -1 };
      }
      if (row.hand) return { line, status: 'complete', label: named(row.hand.name), strength: 1000 + row.hand.ranking + 0.5 };
      if (row.best) {
        const card = axis === 'rank' ? formatCardShort(row.best.card) : formatJewelName(row.best.card.jewel);
        return { line, status: 'partial', label: `${card} → ${named(row.best.hand.name)}`, strength: 1000 + row.best.hand.ranking };
      }
      if (row.wouldBeTakenAway) return { line, status: 'out', label: 'Would be taken away', strength: -1 };
      return { line, status: early?.status ?? 'empty', label: named(early?.label ?? ''), strength: early?.strength ?? 0 };
    })].sort((a, b) => b.strength - a.strength || a.line - b.line);
  };
  const rankPotentials = lineRows('rank');
  const jewelPotentials = lineRows('jewel');

  const taken = (axis: 'rank' | 'jewel') => advice?.taken[axis] ?? null;
  const nextFor = (axis: 'rank' | 'jewel'): string => {
    const a = advice?.[axis];
    if (!a) return '';
    if (a.best) {
      // "Need an A♦ / for a Full House" — not "Next", which read as a prediction of the next card.
      const card = axis === 'rank' ? formatCardShort(a.best.card) : formatJewelName(a.best.card.jewel);
      const name = axis === 'rank' ? a.best.hand.name : dropJewelWord(a.best.hand.name);
      const line = getLineNumber(a.best.hand.winningLine);
      // Not one of the 8 numbered lines: one of Anchor's L-shapes.
      const where = line ? `line ${line}` : 'L-shape';
      // Snare/Fray/Flawed: the card helps by giving the special a better line to take, which
      // frees the line it's taking now.
      const frees = taken(axis) && sameLine(taken(axis)!, a.best.hand.winningLine);
      return `Need ${withArticle(card)} → for ${handWithArticle(name)} · ${frees ? `frees ${where}` : where}`;
    }
    // No single card beats it. All cells known: final. One left and a made hand: it can't be
    // improved. Two or more left: two cards together still might.
    if (a.unknownCells === 0) return 'Final hand';
    if (a.current && a.unknownCells === 1) return "Can't be improved";
    return 'More cards to come';
  };

  // The main hand-name headline drops "Jewel" too — the box's own "Best
  // Color" heading already says which axis this is.
  const jewelEvaluation = jewel ? { ...jewel, name: dropJewelWord(jewel.name) } : null;

  if (compact) {
    // Phones: one box, both hands side by side — each with its tag and name,
    // its 5 cards, and what would improve it. Everything visible, no tapping.
    const half = (
      tag: string,
      tagClass: string,
      nameClass: string,
      hand: { name: string; cards: Card[] } | null,
      lineNo: number | null,
      next: string,
      won?: boolean,
    ) => {
      const [need, rest = ''] = next.split(' → ');
      const [makes, line] = rest.split(' · ');
      return (
        <div className="flex-1 basis-0 min-w-0 min-h-0 flex flex-col justify-center [container-type:inline-size]">
          <div className="flex items-center gap-[0.35em] whitespace-nowrap overflow-hidden" style={{ fontSize: TEXT }}>
            <span className={`shrink-0 px-[0.35em] rounded font-bold uppercase tracking-wide text-[0.8em] ${tagClass}`}>{tag}</span>
            <span className={`truncate font-bold ${hand ? nameClass : 'text-gray-500 italic font-normal'}`}>
              {hand ? hand.name : 'Not yet'}
            </span>
            {hand && (
              <span className="shrink-0 text-[0.7em] text-gray-400">{lineNo !== null ? `line ${lineNo}` : 'L-shape'}</span>
            )}
            {won && <span className="shrink-0">🏆</span>}
          </div>
          {/* The 5 cards, the grid's own card size as far as the half's width allows (each 40% under
              the next: 3.4 card-widths, so 29cqw fits). The grid is sized to leave room for them. */}
          <div className="my-[0.25em] flex items-center">
            {hand?.cards.map((card, i) => (
              <div
                key={i}
                className="w-[min(var(--cell-w,29cqw),29cqw)] shrink-0"
                style={i > 0 ? { marginLeft: 'calc(-0.4 * min(var(--cell-w, 29cqw), 29cqw))' } : undefined}
              >
                <PlayingCard card={card} className="w-full" />
              </div>
            ))}
          </div>
          <div className="leading-[1.2] text-gray-300 [&>div]:whitespace-nowrap [&>div]:overflow-hidden [&>div]:text-ellipsis" style={{ fontSize: TEXT }}>
            <div data-testid="best-hand-next">{need}</div>
            <div>
              {makes || ' '}
              {line && <span className="ml-[0.4em] text-[0.75em] text-gray-400">{line}</span>}
            </div>
          </div>
        </div>
      );
    };
    return (
      <div
        data-testid="best-hand-combined"
        className={`flex-1 min-h-0 overflow-hidden flex gap-3 px-2 py-1.5 bg-gray-900/60 rounded-lg border-2 border-gray-700 ${
          wonRank || wonJewel ? 'ring-2 ring-yellow-400/70' : ''
        }`}
      >
        {half('Hand', 'bg-yellow-600 text-gray-900', 'text-yellow-400', rank, rank ? getLineNumber(rank.winningLine) : null, nextFor('rank'), wonRank)}
        {half('Color', 'bg-fuchsia-600 text-white', 'text-fuchsia-400', jewelEvaluation, jewel ? getLineNumber(jewel.winningLine) : null, nextFor('jewel'), wonJewel)}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 sm:gap-3 h-full">
      {/* Current best hand, both axes — the actual cards, plus that same
          axis's 8-line potential breakdown nested inside each box. Each
          box shows its own half of the split pot, not a shared pot line. */}
      <BestHandDisplay
        label="Best Hand"
        evaluation={rank}
        accentClass="border-yellow-600 text-yellow-400"
        potentials={rankPotentials}
        hint={nextFor('rank')}
        potShare={Math.ceil(pot / 2)}
        lineNumber={rank ? getLineNumber(rank.winningLine) : null}
        won={wonRank}
      />
      <BestHandDisplay
        label="Best Color"
        evaluation={jewelEvaluation}
        accentClass="border-fuchsia-600 text-fuchsia-400"
        potentials={jewelPotentials}
        hint={nextFor('jewel')}
        potShare={Math.floor(pot / 2)}
        lineNumber={jewel ? getLineNumber(jewel.winningLine) : null}
        won={wonJewel}
      />
    </div>
  );
};

export default PlayerPanel;
